if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      purchaseReady = false;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.purchaseReady = false;
        this.initialVariantId = this.querySelector('product-form input[name="id"]')?.value;
        this.initialSubmitDisabled = this.querySelector('product-form [type="submit"]')?.disabled ?? true;
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.onPendingSelectionSubmit = (event) => {
          if (this.dataset.inewerSelectionPending !== 'true' || !event.target.closest('product-form')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        };
        this.addEventListener('submit', this.onPendingSelectionSubmit, true);
        this.setSelectionPending(true);
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        Promise.all([
          customElements.whenDefined('product-form'),
          this.variantSelectors ? customElements.whenDefined('variant-selects') : Promise.resolve(),
        ]).then(() => {
          if (this.isConnected) this.synchronizeInitialSelection();
        });
      }

      selectedOptionInputs() {
        return Array.from(this.variantSelectors?.querySelectorAll('fieldset, .product-form__input--dropdown') || [])
          .map((group) => group.querySelector('input:checked, option:checked'));
      }

      optionTargetsOtherProduct(input) {
        const url = input?.dataset.productUrl;
        if (!url) return false;
        const key = (value) => new URL(value, window.location.origin).pathname.split('/products/').pop().replace(/\/$/, '');
        return key(url) !== key(this.dataset.url);
      }

      canSubmitSelection() {
        if (!this.purchaseReady || this.dataset.inewerSelectionPending === 'true') return false;
        const variant = this.getSelectedVariant(this);
        const inputs = this.selectedOptionInputs();
        if (this.variantSelectors && (!variant?.available || !Array.isArray(variant.options) ||
          inputs.length !== variant.options.length || inputs.some((input, index) =>
            !input || input.value !== variant.options[index] || this.optionTargetsOtherProduct(input)))) return false;
        const id = this.variantSelectors ? variant.id : this.initialVariantId;
        if (!this.variantSelectors && this.initialSubmitDisabled) return false;
        return !!id && String(this.productForm?.variantIdInput?.value) === String(id);
      }

      synchronizeInitialSelection() {
        const picker = this.variantSelectors;
        const inputs = this.selectedOptionInputs();
        const variant = this.getSelectedVariant(this);
        if (picker && (!Array.isArray(variant?.options) || inputs.length !== variant.options.length ||
          inputs.some((input, index) => !input || input.value !== variant.options[index] || this.optionTargetsOtherProduct(input)))) {
          if (!inputs.length || inputs.some((input) => !input?.dataset.optionValueId)) return;
          const target = inputs.find((input) => this.optionTargetsOtherProduct(input)) ||
            inputs.find((input, index) => input.value !== variant?.options?.[index]) || inputs[0];
          inputs.forEach((input) => picker.updateSelectionMetadata({
            target: input.tagName === 'OPTION' ? input.closest('select') : input,
          }));
          this.handleOptionValueChange({ data: {
            event: { target }, target, selectedOptionValues: inputs.map((input) => input.dataset.optionValueId),
          } });
          return;
        }
        this.purchaseReady = true;
        this.setSelectionPending(false);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.abortController?.abort();
        this.removeEventListener('submit', this.onPendingSelectionSubmit, true);
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => this.preserveMobileColorExpansion(html));
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      preserveMobileColorExpansion(html) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        const currentPicker = this.variantSelectors;
        if (!currentPicker) return;
        const nextPicker = html.matches('variant-selects')
          ? html
          : Array.from(html.querySelectorAll('variant-selects')).find(
              (picker) => picker.dataset.section === currentPicker.dataset.section
            );
        if (!nextPicker || nextPicker.dataset.section !== currentPicker.dataset.section) return;

        // Transfer state into the incoming markup before viewTransition clones it.
        currentPicker.querySelectorAll('.inewer-mobile-color-collapse').forEach((group) => {
          const button = group.querySelector('[data-inewer-mobile-color-toggle]');
          const optionName = group.querySelector('input[data-option-name]')?.dataset.optionName;
          if (!button || optionName !== 'Color') return;

          const nextGroup = Array.from(nextPicker.querySelectorAll('.inewer-mobile-color-collapse')).find(
            (candidate) => candidate.querySelector('input[data-option-name]')?.dataset.optionName === optionName
          );
          const nextButton = nextGroup?.querySelector('[data-inewer-mobile-color-toggle]');
          if (!nextButton) return;

          const expanded = group.classList.contains('is-expanded');
          nextGroup.classList.toggle('is-expanded', expanded);
          nextButton.setAttribute('aria-expanded', String(expanded));
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
        this.setSelectionPending(true);
        this.querySelector('[data-inewer-selection-error]')?.setAttribute('hidden', '');
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      setSelectionPending(pending) {
        this.dataset.inewerSelectionPending = String(pending);
        const input = this.productForm?.variantIdInput;
        if (input) input.disabled = !this.canSubmitSelection();
        this.querySelectorAll('.shopify-payment-button').forEach((element) => {
          if (pending && !element.hasAttribute('inert')) {
            element.setAttribute('inert', '');
            element.dataset.inewerSelectionInert = 'true';
          } else if (!pending && element.dataset.inewerSelectionInert === 'true') {
            element.removeAttribute('inert');
            delete element.dataset.inewerSelectionInert;
          }
        });
      }

      showSelectionError(request) {
        this.failedSelectionRequest = request;
        this.productForm?.toggleSubmitButton(true);
        let notice = this.querySelector('[data-inewer-selection-error]');
        if (!notice) {
          notice = document.createElement('div');
          notice.setAttribute('data-inewer-selection-error', '');
          notice.setAttribute('role', 'alert');
          const message = document.createElement('p');
          message.textContent = 'Your selection could not be updated. Please retry or choose another option.';
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'button button--secondary';
          retry.textContent = 'Retry';
          retry.addEventListener('click', () => {
            if (this.failedSelectionRequest) this.renderProductInfo(this.failedSelectionRequest);
          });
          notice.append(message, retry);
          if (this.variantSelectors) this.variantSelectors.after(notice);
          else this.prepend(notice);
        }
        notice.removeAttribute('hidden');
      }

      renderProductInfo(request) {
        const { requestUrl, targetId, callback } = request;
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.failedSelectionRequest = null;
        this.resetProductFormState();
        const preserveMobileScroll = window.matchMedia('(max-width: 749px)').matches;
        let timedOut = false;
        const timeout = window.setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, 20000);

        return fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product update failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            // An aborted or superseded selection must never update the current product.
            if (controller.signal.aborted || this.abortController !== controller) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const source = html.querySelector('product-info');
            if (!source) throw new Error('Product response is incomplete');
            const selectors = source.querySelector('variant-selects');
            if (this.variantSelectors && !selectors && source.dataset.url === this.dataset.url) {
              throw new Error('Product options are missing');
            }
            if (selectors) {
              const selected = selectors.querySelector('[data-selected-variant]');
              if (!selected) throw new Error('Product variant data is missing');
              // JSON null is a valid unavailable combination; malformed/missing JSON is not.
              const variant = JSON.parse(selected.textContent);
              if (variant !== null && (typeof variant !== 'object' || Array.isArray(variant) || !variant.id)) {
                throw new Error('Product variant data is invalid');
              }
            }
            this.pendingRequestUrl = null;
            callback(html);
            this.purchaseReady = true;
            this.setSelectionPending(false);
            if (!preserveMobileScroll) {
              document.querySelector(`#${CSS.escape(targetId)}`)?.focus({ preventScroll: true });
            }
          })
          .catch((error) => {
            if (this.abortController !== controller || (error.name === 'AbortError' && !timedOut)) return;
            this.pendingRequestUrl = null;
            // Keep the previous variant ID and price intact, but block purchasing until a valid response arrives.
            this.showSelectionError(request);
            this.variantSelectors?.rejectPendingSelectPromise(error);
          })
          .finally(() => window.clearTimeout(timeout));
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          const previousVariant = this.getSelectedVariant(this);
          const previousColor = this.getGalleryColor(this, previousVariant);
          const nextColor = this.getGalleryColor(html, variant);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id, previousVariant?.featured_media?.id, previousColor, nextColor);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const currentUrl = new URL(window.location.href);
        const targetUrl = new URL(url, window.location.origin);
        const currentCollectionProduct = currentUrl.pathname.match(/^\/collections\/[^/]+\/products\/([^/]+)\/?$/);
        const targetProduct = targetUrl.pathname.match(/^\/products\/([^/]+)\/?$/);

        if (currentCollectionProduct && targetProduct && currentCollectionProduct[1] === targetProduct[1]) {
          targetUrl.pathname = currentUrl.pathname;
          targetUrl.search = currentUrl.search;
        }

        targetUrl.searchParams.delete('option_values');
        if (variantId) {
          targetUrl.searchParams.set('variant', variantId);
        } else {
          targetUrl.searchParams.delete('variant');
        }

        const relativeUrl = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${relativeUrl}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', relativeUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      getGalleryColor(root, variant) {
        const groups = this.getVariantSelects(root)?.querySelectorAll('fieldset, .product-form__input--dropdown');
        if (!groups || !Array.isArray(variant?.options)) return null;
        const index = Array.from(groups).findIndex((group) => {
          const name = group.querySelector('[data-option-name]')?.dataset.optionName?.trim().toLowerCase();
          return name === 'color' || name === 'colour';
        });
        return index >= 0 && typeof variant.options[index] === 'string' ? variant.options[index] : null;
      }

      mediaListState(list) {
        if (!list) return null;
        const state = [];
        for (const item of Array.from(list.children)) {
          const key = item.dataset.mediaId || item.dataset.target;
          if (!key) return null;
          const images = item.matches('img') ? [item] : Array.from(item.querySelectorAll('img'));
          const templates = Array.from(item.querySelectorAll('template'));
          // Compare authored resources, not player/zoom state added after opening media.
          if (!images.length && !templates.length) return null;
          state.push([key, item.tagName,
            images.map((image) => ['src', 'srcset', 'sizes', 'alt', 'width', 'height'].map((name) => image.getAttribute(name))),
            templates.map((template) => template.innerHTML)]);
        }
        return JSON.stringify(state);
      }

      updateMedia(html, variantFeaturedMediaId, previousFeaturedMediaId, previousColor, nextColor) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery');
        const mediaGalleryDestination = html.querySelector('media-gallery');

        const currentList = mediaGallerySource?.querySelector('.product__media-list');
        const nextList = mediaGalleryDestination?.querySelector('.product__media-list');
        const currentThumbnails = mediaGallerySource?.querySelector('.thumbnail-list');
        const nextThumbnails = mediaGalleryDestination?.querySelector('.thumbnail-list');
        const listState = this.mediaListState(currentList);
        const thumbnailState = this.mediaListState(currentThumbnails);
        const nextThumbnailState = this.mediaListState(nextThumbnails);
        const priorState = this.inewerGalleryState;
        // The existing gallery copies the full-size front image into its first thumbnail.
        // Accept that known owned state only while both live and response resources still match.
        const sameThumbnails = thumbnailState !== null && nextThumbnailState !== null &&
          (thumbnailState === nextThumbnailState || (priorState?.gallery === mediaGallerySource &&
            priorState.liveThumbnails === thumbnailState && priorState.responseThumbnails === nextThumbnailState));
        const activeMedia = mediaGallerySource?.querySelector('.product__media-item.is-active[data-media-id]');
        const sameGallery = previousColor !== null && previousColor === nextColor &&
          previousFeaturedMediaId != null && String(previousFeaturedMediaId) === String(variantFeaturedMediaId) &&
          listState !== null && listState === this.mediaListState(nextList) &&
          sameThumbnails &&
          activeMedia && Array.from(nextList.children).some((item) => item.dataset.mediaId === activeMedia.dataset.mediaId);

        const reconcileMediaList = (sourceList, destinationList) => {
          if (!sourceList || !destinationList) return;

          let sourceItems = Array.from(sourceList.querySelectorAll(':scope > li[data-media-id], :scope > li[data-target]'));
          const destinationItems = Array.from(
            destinationList.querySelectorAll(':scope > li[data-media-id], :scope > li[data-target]')
          );
          const itemKey = (item) => item.dataset.mediaId || item.dataset.target;
          const destinationKeys = new Set(destinationItems.map(itemKey));

          sourceItems.forEach((item) => {
            if (!destinationKeys.has(itemKey(item))) item.remove();
          });

          destinationItems.forEach((destinationItem, destinationIndex) => {
            const key = itemKey(destinationItem);
            sourceItems = Array.from(sourceList.querySelectorAll(':scope > li[data-media-id], :scope > li[data-target]'));
            let sourceItem = sourceItems.find((item) => itemKey(item) === key);
            if (!sourceItem) sourceItem = destinationItem.cloneNode(true);

            const currentItem = sourceList.children[destinationIndex];
            if (currentItem !== sourceItem) sourceList.insertBefore(sourceItem, currentItem || null);
          });
        };

        if (!sameGallery && mediaGallerySource && mediaGalleryDestination) {
          reconcileMediaList(
            mediaGallerySource.querySelector('.product__media-list'),
            mediaGalleryDestination.querySelector('.product__media-list')
          );
          reconcileMediaList(
            mediaGallerySource.querySelector('.thumbnail-list'),
            mediaGalleryDestination.querySelector('.thumbnail-list')
          );
          mediaGallerySource.refreshMediaItems?.();
        }

        // Keep every gallery UI surface on the selected variant's featured media.
        if (!sameGallery) mediaGallerySource?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        if (mediaGallerySource) this.inewerGalleryState = {
          gallery: mediaGallerySource,
          liveThumbnails: this.mediaListState(currentThumbnails),
          responseThumbnails: nextThumbnailState,
        };

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) {
          const modalState = this.mediaListState(modalContent);
          if (modalState === null || modalState !== this.mediaListState(newModalContent)) {
            modalContent.innerHTML = newModalContent.innerHTML;
          }
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
